import React, { useState, useEffect, useMemo, useRef } from "react";
import Header from "./Header.js";
import Footer from "./Footer.js";
import { TailSpin } from "react-loader-spinner";
import {
  getAdminInstructor,
  getWorkshoplistById,
  getWorkshopSlotByWorkshopId,
  getAppointmentsByWorkshopSlot,
  updateCustomerCancelForm,
} from "../../Api/apiUrls.js";

function Attendance() {
  const [instructors, setInstructors] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [workshopSlots, setWorkshopSlots] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedInstructor, setSelectedInstructor] = useState("");
  const [selectedWorkshop, setSelectedWorkshop] = useState("");
  const [selectedWorkshopSlot, setSelectedWorkshopSlot] = useState("");
  const [instructorSearch, setInstructorSearch] = useState("");
  const [workshopSearch, setWorkshopSearch] = useState("");
  const [workshopSlotSearch, setWorkshopSlotSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState({
    instructor: false,
    workshop: false,
    slot: false,
  });

  const dropdownRefs = {
    instructor: useRef(null),
    workshop: useRef(null),
    slot: useRef(null),
  };

  const formatDate = (dateString) => {
    const options = { day: "numeric", month: "short", year: "numeric" };
    return new Date(dateString).toLocaleDateString("en-GB", options);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const newState = { ...dropdownOpen };
      let changed = false;

      Object.keys(dropdownRefs).forEach((key) => {
        if (
          dropdownRefs[key].current &&
          !dropdownRefs[key].current.contains(event.target) &&
          dropdownOpen[key]
        ) {
          newState[key] = false;
          changed = true;
        }
      });

      if (changed) {
        setDropdownOpen(newState);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Filtered lists based on search terms
  const filteredInstructors = useMemo(() => {
    return instructors.filter((instructor) =>
      instructor.name?.toLowerCase().includes(instructorSearch.toLowerCase()) ||
      instructor.email?.toLowerCase().includes(instructorSearch.toLowerCase())
    );
  }, [instructors, instructorSearch]);

  const filteredWorkshops = useMemo(() => {
    return workshops.filter((workshop) =>
      workshop.name?.toLowerCase().includes(workshopSearch.toLowerCase())
    );
  }, [workshops, workshopSearch]);

  const filteredWorkshopSlots = useMemo(() => {
    return workshopSlots.filter((slot) => {
      const dateStr = formatDate(slot.date);
      const timeStr = `${slot.startTime} to ${slot.endTime}`;
      return (
        dateStr.toLowerCase().includes(workshopSlotSearch.toLowerCase()) ||
        timeStr.toLowerCase().includes(workshopSlotSearch.toLowerCase())
      );
    });
  }, [workshopSlots, workshopSlotSearch]);

  // Fetch instructors on component mount
  useEffect(() => {
    const fetchInstructors = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(getAdminInstructor);
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();
        setInstructors(data.data.users || []);
      } catch (error) {
        console.error("Error fetching instructors:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInstructors();
  }, []);

  // Fetch workshops when instructor is selected
  useEffect(() => {
    const fetchWorkshops = async () => {
      if (!selectedInstructor) return;
      setIsLoading(true);
      try {
        const response = await fetch(
          `${getWorkshoplistById}${selectedInstructor}`
        );
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();
        setWorkshops(data.data.workshops || []);
        setSelectedWorkshop("");
        setWorkshopSlots([]);
        setSelectedWorkshopSlot("");
        setAppointments([]);
        setWorkshopSearch("");
      } catch (error) {
        console.error("Error fetching workshops:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkshops();
  }, [selectedInstructor]);

  // Fetch workshop slots when workshop is selected
  useEffect(() => {
    const fetchWorkshopSlots = async () => {
      if (!selectedWorkshop) return;
      setIsLoading(true);
      try {
        const response = await fetch(
          `${getWorkshopSlotByWorkshopId}${selectedWorkshop}`
        );
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();
        setWorkshopSlots(data.data.WorkshopSlot || []);
        setSelectedWorkshopSlot("");
        setAppointments([]);
        setWorkshopSlotSearch("");
      } catch (error) {
        console.error("Error fetching workshop slots:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkshopSlots();
  }, [selectedWorkshop]);

  // Fetch appointments when workshop slot is selected
  useEffect(() => {
    const fetchAppointments = async () => {
      if (!selectedWorkshopSlot) return;
      setIsLoading(true);
      try {
        const response = await fetch(
          `${getAppointmentsByWorkshopSlot}?workshop_slot_id=${selectedWorkshopSlot}`
        );
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();
        
        // Sort by attendance status: Not Marked first, then Attended, then Not Attended
        const sortedAppointments = [...(data.data.Appointments || [])].sort((a, b) => {
          if (a.attended === null && b.attended !== null) return -1;
          if (a.attended !== null && b.attended === null) return 1;
          if (a.attended === true && b.attended === false) return -1;
          if (a.attended === false && b.attended === true) return 1;
          return 0;
        });
        setAppointments(sortedAppointments);
      } catch (error) {
        console.error("Error fetching appointments:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAppointments();
  }, [selectedWorkshopSlot]);

  const toggleDropdown = (dropdown) => {
    setDropdownOpen((prev) => ({
      ...prev,
      [dropdown]: !prev[dropdown],
    }));
  };

  const handleInstructorSelect = (instructorId) => {
    setSelectedInstructor(instructorId);
    setDropdownOpen((prev) => ({ ...prev, instructor: false }));
  };

  const handleWorkshopSelect = (workshopId) => {
    setSelectedWorkshop(workshopId);
    setDropdownOpen((prev) => ({ ...prev, workshop: false }));
  };

  const handleWorkshopSlotSelect = (slotId) => {
    setSelectedWorkshopSlot(slotId);
    setDropdownOpen((prev) => ({ ...prev, slot: false }));
  };

  const handleAttendanceChange = async (appointmentId, value) => {
    try {
      let attended = false;
      let not_attended = false;
      
      if (value === "attended") {
        attended = true;
        not_attended = false;
      } else if (value === "not_attended") {
        attended = false;
        not_attended = true;
      } else {
        attended = false;
        not_attended = false;
      }
      
      const response = await fetch(updateCustomerCancelForm, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Appointment_id: appointmentId,
          attended: attended,
          not_attended: not_attended,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update attendance.");
      }

      const data = await response.json();

      if (data.success) {
        setAppointments((prevAppointments) =>
          prevAppointments
            .map((appointment) =>
              appointment.id === appointmentId
                ? { 
                    ...appointment, 
                    attended: attended,
                    not_attended: not_attended 
                  }
                : appointment
            )
            .sort((a, b) => {
              if (a.attended === null && b.attended !== null) return -1;
              if (a.attended !== null && b.attended === null) return 1;
              if (a.attended === true && b.attended === false) return -1;
              if (a.attended === false && b.attended === true) return 1;
              return 0;
            })
        );
      } else {
        alert("Failed to update attendance. Please try again.");
      }
    } catch (error) {
      console.error("Error updating attendance:", error);
      alert("Failed to update attendance. Please try again.");
    }
  };

  const getDisplayName = (user) => {
    return user?.name || user?.email || "Unknown";
  };

  const getSelectedInstructorName = () => {
    const instructor = instructors.find((i) => i.id === selectedInstructor);
    return instructor ? getDisplayName(instructor) : "Select Instructor";
  };

  const getSelectedWorkshopName = () => {
    const workshop = workshops.find((w) => w.id === selectedWorkshop);
    return workshop ? workshop.name : "Select Workshop";
  };

  const getSelectedWorkshopSlotName = () => {
    const slot = workshopSlots.find((s) => s.id === selectedWorkshopSlot);
    return slot
      ? `${formatDate(slot.date)} - ${slot.startTime} to ${slot.endTime}`
      : "Select Workshop Slot";
  };

  const getStatusBadge = (attended, not_attended) => {
    if (attended === true) return <span className="badge bg-success">Attended</span>;
    if (not_attended === true) return <span className="badge bg-danger">Not Attended</span>;
    return <span className="badge bg-secondary">Not Marked</span>;
  };

  return (
    <div>
      <Header />
      <main id="main" className="main">
        <div className="pagetitle">
          <h1>Attendance</h1>
        </div>
        <section className="section">
          <div className="card p-2">
            <div className="row mt-2">
              {/* Instructor Dropdown */}
              <div className="col-md-3">
                <div className="dropdown" ref={dropdownRefs.instructor}>
                  <button
                    className="btn btn-secondary dropdown-toggle w-100 text-start d-flex align-items-center justify-content-between"
                    type="button"
                    onClick={() => toggleDropdown("instructor")}
                    disabled={isLoading}
                  >
                    <span className="text-truncate">
                      {getSelectedInstructorName()}
                    </span>
                  </button>
                  <div
                    className={`dropdown-menu w-100 p-0 ${dropdownOpen.instructor ? "show" : ""}`}
                    style={{ maxHeight: "300px", overflow: "auto" }}
                  >
                    <div className="p-2">
                      <input
                        type="text"
                        className="form-control mb-2"
                        placeholder="Search instructors..."
                        value={instructorSearch}
                        onChange={(e) => setInstructorSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {filteredInstructors.length > 0 ? (
                      filteredInstructors.map((instructor) => (
                        <button
                          key={instructor.id}
                          className={`dropdown-item d-flex align-items-center ${selectedInstructor === instructor.id ? "active" : ""}`}
                          onClick={() => handleInstructorSelect(instructor.id)}
                        >
                          {instructor.profile_pic && (
                            <img
                              src={instructor.profile_pic}
                              alt={getDisplayName(instructor)}
                              className="rounded-circle me-2"
                              width="24"
                              height="24"
                            />
                          )}
                          <div className="text-truncate">
                            {getDisplayName(instructor)}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="dropdown-item text-muted">
                        No instructors found
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Workshop Dropdown */}
              <div className="col-md-3">
                <div className="dropdown" ref={dropdownRefs.workshop}>
                  <button
                    className="btn btn-secondary dropdown-toggle w-100 text-start d-flex align-items-center justify-content-between"
                    type="button"
                    onClick={() => toggleDropdown("workshop")}
                    disabled={!selectedInstructor || isLoading}
                  >
                    <span className="text-truncate">
                      {getSelectedWorkshopName()}
                    </span>
                  </button>
                  <div
                    className={`dropdown-menu w-100 p-0 ${dropdownOpen.workshop ? "show" : ""}`}
                    style={{ maxHeight: "300px", overflow: "auto" }}
                  >
                    <div className="p-2">
                      <input
                        type="text"
                        className="form-control mb-2"
                        placeholder="Search workshops..."
                        value={workshopSearch}
                        onChange={(e) => setWorkshopSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {filteredWorkshops.length > 0 ? (
                      filteredWorkshops.map((workshop) => (
                        <button
                          key={workshop.id}
                          className={`dropdown-item ${selectedWorkshop === workshop.id ? "active" : ""}`}
                          onClick={() => handleWorkshopSelect(workshop.id)}
                        >
                          <div className="text-truncate">{workshop.name}</div>
                        </button>
                      ))
                    ) : (
                      <div className="dropdown-item text-muted">
                        No workshops found
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Workshop Slot Dropdown */}
              <div className="col-md-3">
                <div className="dropdown" ref={dropdownRefs.slot}>
                  <button
                    className="btn btn-secondary dropdown-toggle w-100 text-start d-flex align-items-center justify-content-between"
                    type="button"
                    onClick={() => toggleDropdown("slot")}
                    disabled={!selectedWorkshop || isLoading}
                  >
                    <span className="text-truncate">
                      {getSelectedWorkshopSlotName()}
                    </span>
                  </button>
                  <div
                    className={`dropdown-menu w-100 p-0 ${dropdownOpen.slot ? "show" : ""}`}
                    style={{ maxHeight: "300px", overflow: "auto" }}
                  >
                    <div className="p-2">
                      <input
                        type="text"
                        className="form-control mb-2"
                        placeholder="Search slots..."
                        value={workshopSlotSearch}
                        onChange={(e) => setWorkshopSlotSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {filteredWorkshopSlots.length > 0 ? (
                      filteredWorkshopSlots.map((slot) => (
                        <button
                          key={slot.id}
                          className={`dropdown-item ${selectedWorkshopSlot === slot.id ? "active" : ""}`}
                          onClick={() => handleWorkshopSlotSelect(slot.id)}
                        >
                          {formatDate(slot.date)} - {slot.startTime} to{" "}
                          {slot.endTime}
                        </button>
                      ))
                    ) : (
                      <div className="dropdown-item text-muted">
                        No slots found
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {isLoading ? (
              <div className="d-flex justify-content-center mt-5">
                <TailSpin color="black" width={50} />
              </div>
            ) : selectedWorkshopSlot ? (
              <div className="table-responsive mt-4">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th scope="col">Sr.No</th>
                      <th scope="col">User Name</th>
                      <th scope="col">Email</th>
                      <th scope="col">Attendance</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointments.map((appointment, index) => (
                      <tr key={appointment.id}>
                        <td>{index + 1}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            {appointment.user.profile_pic && (
                              <img
                                src={appointment.user.profile_pic}
                                alt={getDisplayName(appointment.user)}
                                className="rounded-circle me-2"
                                width="32"
                                height="32"
                              />
                            )}
                            {getDisplayName(appointment.user)}
                          </div>
                        </td>
                        <td>{appointment.user.email}</td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={
                              appointment.attended === true 
                                ? "attended" 
                                : appointment.not_attended === true 
                                  ? "not_attended" 
                                  : "not_marked"
                            }
                            onChange={(e) => handleAttendanceChange(appointment.id, e.target.value)}
                          >
                            <option value="not_marked">Not Marked</option>
                            <option value="attended">Attended</option>
                            <option value="not_attended">Not Attended</option>
                          </select>
                        </td>
                        <td>
                          {getStatusBadge(appointment.attended, appointment.not_attended)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-center bg-dark text-white p-2">
                  Total: {appointments.length} | Attended:{" "}
                  {appointments.filter((a) => a.attended === true).length} | 
                  Not Attended: {appointments.filter((a) => a.not_attended === true).length} | 
                  Not Marked: {appointments.filter((a) => a.attended === null && a.not_attended === null).length}
                </p>
              </div>
            ) : (
              <h3 className="text-center mt-4">
                {selectedInstructor && selectedWorkshop
                  ? "Please select a workshop slot"
                  : "Please select an instructor and workshop"}
              </h3>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default Attendance;